import React from 'react';
import { Link } from 'react-router-dom';
import { MyUnitCodeDto } from '../../types/unit';
import { BookOpen, Play, GraduationCap } from 'lucide-react';
import { BASE_URL } from '../../apiConfig';

interface UnitCardProps {
  unit: MyUnitCodeDto;
}

const getUnitImageUrl = (unit: MyUnitCodeDto): string => {
  if (!unit.imagePath) return '/default-course.jpg';
  if (unit.imagePath.startsWith('http')) return unit.imagePath;
  const path = unit.imagePath.startsWith('/') ? unit.imagePath.slice(1) : unit.imagePath;
  return `${BASE_URL}${path}`;
};

const UnitCard: React.FC<UnitCardProps> = ({ unit }) => {
  const getGradeInArabic = (grade: string) => {
    switch (grade) {
      case 'Secondary1': return 'الصف الأول الثانوي';
      case 'Secondary2': return 'الصف الثاني الثانوي';
      case 'Secondary3': return 'الصف الثالث الثانوي';
      default: return grade;
    }
  };

  const getTermInArabic = (term: string) => {
    switch (term) {
      case 'First': return 'الترم الأول';
      case 'Second': return 'الترم الثاني';
      default: return term;
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
      {/* Image */}
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-green-50">
        <img
          src={getUnitImageUrl(unit)}
          alt={`${unit.unitName} - ${unit.unitTitle}`}
          title={unit.unitName}
          className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 ease-out hover:scale-105"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.src = '/default-course.jpg';
          }}
        />
        {/* Status badge overlay */}
        <span className={`absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full text-xs font-bold ${
          unit.active 
            ? 'bg-green-500 text-white' 
            : 'bg-gray-500 text-white'
        }`}>
          {unit.active ? 'نشط' : 'غير نشط'}
        </span>
      </div>
      
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 p-3 sm:p-4 text-white">
        <div className="flex items-center justify-between">
          <h3 className="text-base sm:text-lg font-bold line-clamp-1">{unit.unitName}</h3>
          <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
        </div>
      </div>
      
      {/* Content */}
      <div className="p-3 sm:p-4">
        {/* Course info */}
        {unit.courseName && (
          <div className="flex items-center gap-2 text-gray-600 mb-2">
            <GraduationCap className="w-4 h-4 shrink-0" />
            <span className="text-xs sm:text-sm line-clamp-1">{unit.courseName}</span>
          </div>
        )}
        
        {/* Grade and Term */}
        {(unit.grade || unit.term) && (
        <div className="flex flex-wrap gap-2 mb-3 sm:mb-4">
          {unit.grade && (
          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-medium">
            {getGradeInArabic(unit.grade)}
          </span>
          )}
          {unit.term && (
          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium">
            {getTermInArabic(unit.term)}
          </span>
          )}
        </div>
        )}
        
        {/* Action button */}
        <Link 
          to={`/unit-viewer/${unit.unitId}`}
          className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 min-h-11 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium text-sm transition-colors duration-200"
        >
          <span>دخول الحصة</span>
          <Play className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
};

export default React.memo(UnitCard); 